/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Input, OnInit, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import * as _ from 'lodash';

import template from './semantic-targeting.component.html';
import {ADN_TARGETING_TYPES, CURRENCY_OR_COUNTRY_TO_LANG} from '../targeting-constants';
import {doLevelMatch, semanticMatchLevels} from './semantic-targeting.util';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {TargetingSearchApiService} from '../targeting-search-api.service';

import {TARGETING_IMPORTS} from '../targeting-imports';
import {SentimentResultComponent} from '../../common/controller/sentiment-result-component';

@Component({
  imports: [...TARGETING_IMPORTS, SentimentResultComponent],
  selector: 'adn-semantic-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SemanticTargetingComponent implements OnInit {
  @Input() model: any[];
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.semantic;
  @Input() underModel: any;
  @Input() modelLineItem: any;
  @Input() runningModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  loaded = true;
  private readonly embeddingState = signal(false);
  get embedding(): boolean { return this.embeddingState(); }
  set embedding(value: boolean) { this.embeddingState.set(value); }
  resultsIndex: number;
  private readonly listState = signal<any>({});
  get list(): any { return this.listState(); }
  set list(value: any) { this.listState.set(value); }
  totalHitsByIndex: Record<number, boolean> = {};

  readonly matchLevels = semanticMatchLevels;

  private readonly searchMetaState = signal<any>({
    pageSize: 100,
    currentPage: 1,
    thresholdScore: 0.5,
    positiveSentimentOnly: false,
    resolvedError: '',
    resolved: true,
    sentence: '',
    totalHits: false
  }, {equal: () => false});
  get searchMeta(): any { return this.searchMetaState(); }
  set searchMeta(value: any) { this.searchMetaState.set(value); }

  private isPreparingForSave: Promise<void>;

  constructor(private readonly targetingSearchApi: TargetingSearchApiService) {
  }

  ngOnInit(): void {
    if (!_.isArray(this.model)) {
      return;
    }

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.semantic.widget, () => {
      Promise.resolve(this.isPreparingForSave).then(() => {
        _.forEach(this.model, (s: any) => {
          if (s.score) {
            s.managedScore = doLevelMatch(s.score);
          }
        });
        if (this.model.length === 0) {
          this.model.push({});
        }
        this.searchMeta = this.searchMeta;
      });
    }, copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  addNew(): void {
    this.model.push({});
  }

  editTarget(semTar: any): void {
    _.forEach(this.model, (sem: any) => {
      if (_.isEqual(semTar, sem)) {
        semTar.editable = !semTar.editable;
      } else {
        sem.editable = false;
      }
    });
    if (semTar.editable) {
      const num = _.findIndex(this.model, (sem: any) => {
        return !_.get(sem, ['sentence', 'sentence']);
      });
      if (num > -1) {
        _.pullAt(this.model, num);
      }
      this.list = {};
      // One-shot assign — avoid clear-then-set on [(ngModel)] searchMeta.sentence (NG0100).
      this.searchMeta = {
        pageSize: 100,
        currentPage: 1,
        thresholdScore: semTar.score,
        positiveSentimentOnly: semTar.positiveSentimentOnly,
        resolvedError: '',
        resolved: true,
        sentence: semTar.sentence.sentence,
        totalHits: false
      };
    } else {
      // Cancel edit — defer keywords → '' so the textarea is not mid-check (NG0100).
      deferSignalStateWrite(() => {
        this.resetSearchAndInputFields();
      });
    }
  }

  removeTarget(index: number): void {
    _.pullAt(this.model, index);

    if (this.model.length === 0) {
      this.model.push({});
    }
    deferSignalStateWrite(() => {
      this.resetSearchAndInputFields();
    });
  }

  cancelResults(): void {
    this.list = {};
  }

  addToTargeting(negated: boolean, index: number): Promise<void> {
    if (!_.isString(this.searchMeta.sentence) || _.trim(this.searchMeta.sentence).length < 2 || !this.searchMeta.thresholdScore) {
      return Promise.resolve();
    }
    this.embedding = true;
    return firstValueFrom(this.targetingSearchApi.createSemanticEmbedding({sentence: this.searchMeta.sentence})).then((page) => {
      const modelEntry = {
        score: this.searchMeta.thresholdScore,
        negated: !!negated,
        sentence: page,
        positiveSentimentOnly: this.searchMeta.positiveSentimentOnly,
        managedScore: doLevelMatch(this.searchMeta.thresholdScore)
      };
      // Defer whole settle — Add New [disabled]="!model[last]?.sentence" and
      // textarea sentence clear both flip mid-CD otherwise (NG0100).
      return new Promise<void>((resolve) => {
        deferSignalStateWrite(() => {
          if (this.model.length === 0) {
            this.model.push(modelEntry);
          } else {
            this.model[index] = modelEntry;
          }
          this.resetSearch();
          this.embedding = false;
          resolve();
        });
      });
    });
  }

  search(semOrIndex?: any, index?: number): void {
    this.resultsIndex = _.isFinite(semOrIndex) && !_.isObject(semOrIndex) ? semOrIndex : index;
    this.searchMeta.currentPage = 1;
    const searchParams: any = {
      sentence: _.get(semOrIndex, ['sentence', 'sentence'], this.searchMeta.sentence || ''),
      thresholdScore: _.get(semOrIndex, ['score'], this.searchMeta.thresholdScore),
      positiveSentimentOnly: this.searchMeta.positiveSentimentOnly,
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage,
      concise: true
    };

    // Defer — [disabled]="!searchMeta.resolved" true→false on settle (NG0100).
    deferSignalStateWrite(() => {
      this.searchMeta.resolved = false;
      this.searchMeta = this.searchMeta;
    });
    searchParams.page = this.searchMeta.page;
    searchParams.languages = '';

    const countriesSelected = this.runningModel[ADN_TARGETING_TYPES.siteCountry.targets][ADN_TARGETING_TYPES.siteCountry.apiProp];
    if (_.isArray(countriesSelected)) {
      let languages: string[] = [];
      _.forEach(countriesSelected, (code: string) => {
        const langs = CURRENCY_OR_COUNTRY_TO_LANG[code];
        if (langs) {
          languages = languages.concat(langs);
        }
      });
      searchParams.languages = languages.join(';');
    }

    if (_.isFinite(index) && this.totalHitsByIndex[index]) {
      searchParams.totalHits = true;
    } else if (this.searchMeta.totalHits) {
      searchParams.totalHits = true;
    }

    if (!searchParams.languages) {
      const lineItemId = _.get(this.modelLineItem, ['id'], undefined);
      if (lineItemId) {
        const productName = _.get(this.modelLineItem, ['axProduct', 'name']) || '';
        const productCurrency = productName.indexOf('Norway') > -1 ? 'NOK' :
          productName.indexOf('Sweden') > -1 ? 'SEK' :
            productName.indexOf('Denmark') > -1 ? 'DKK' :
              productName.indexOf('Danmark') > -1 ? 'DKK' : undefined;
        const languages = CURRENCY_OR_COUNTRY_TO_LANG[productCurrency];
        if (_.isArray(languages)) {
          searchParams.languages = languages.join(';');
        } else {
          searchParams.lineItemId = lineItemId;
        }
      }
    }

    const runningModel = _.cloneDeep(this.runningModel);
    delete runningModel[ADN_TARGETING_TYPES.semantic.targets];
    this.searchMeta.count = 0;

    this.targetingSearchApi.searchArticles(runningModel, searchParams).subscribe((page) => {
      deferSignalStateWrite(() => {
        page.totalHits = searchParams.totalHits;
        let count = 0;
        _.forEach(page.results, (p: any) => {
          const profile = p;
          profile.count = 0;
          profile.managedScore = doLevelMatch(p.score);
          const firstSlash = profile.url.indexOf('/');
          profile.urlSplit = firstSlash > 0 ? [profile.url.substr(0, firstSlash), profile.url.substring(firstSlash)] : [];
          profile.title = profile.title || 'No title found';
          profile.description = profile.description || 'No description found';
          count += (p.availableImpressionsLastDay || 0);
          const imgUrl = profile.image;
          profile.image = (imgUrl && _.startsWith(imgUrl, 'http')) ? imgUrl : '';
        });
        const resultCount = page.results.length;
        this.list = page;
        this.searchMeta.resultCount = resultCount;
        this.searchMeta.count = count;
        this.searchMeta.resolved = true;
      });
    });
  }

  private resetSearch(): void {
    this.searchMeta = {
      pageSize: 100,
      currentPage: 1,
      thresholdScore: 0.5,
      positiveSentimentOnly: false,
      resolvedError: '',
      resolved: true,
      sentence: '',
      totalHits: false
    };
  }

  private resetSearchAndInputFields(): void {
    this.resetSearch();
    this.list = {};
  }

  private prepareForSave(): Promise<void> {
    const num = _.findIndex(this.model, (sem: any) => {
      return sem.editable;
    });
    this.isPreparingForSave = this.addToTargeting(false, num >= 0 ? num : (this.model.length - 1)).then(() => {
      const indicesForRemoval = _.filter(_.map(this.model, (sem: any, index: number) => {
        return _.get(sem, ['sentence', 'sentence']) ? -1 : index;
      }), (i: number) => {
        return i > -1;
      });
      _.pullAt(this.model, indicesForRemoval);
      _.forEach(this.model, (s: any) => {
        delete s.managedScore;
      });
    });
    return this.isPreparingForSave;
  }

  private copyTemplate(tpl: any): void {
    if (!_.isArray(this.model)) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    if (this.model.length === 0) {
      this.model.push({});
    }
    deferSignalStateWrite(() => {
      this.resetSearchAndInputFields();
    });
  }
}
